import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardAvoidingView, Linking, Platform, Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AgentCard as AgentCardData, fetchAgentApi, requestProtectionQuoteApi } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { TextField } from '@/components/ui/text-field';
import { useAuthStore } from '@/store/auth-store';
import { colors, radius, spacing } from '@/theme/tokens';

// Credit ask, structured so the lead reaches the office with a real starting
// point (owner: "zapytaj o rodzaj kredytu, cel, kwotę i notatkę").
type CreditKind = 'MORTGAGE' | 'CASH';
const CREDIT_PURPOSES: Record<CreditKind, { key: string; labelKey: string }[]> = {
  MORTGAGE: [
    { key: 'BUY_FLAT', labelKey: 'agent.creditPurposeBuyFlat' },
    { key: 'BUY_HOUSE', labelKey: 'agent.creditPurposeBuyHouse' },
    { key: 'BUILD', labelKey: 'agent.creditPurposeBuild' },
    { key: 'REFINANCE', labelKey: 'agent.creditPurposeRefinance' },
    { key: 'OTHER', labelKey: 'agent.creditPurposeOther' },
  ],
  CASH: [
    { key: 'CASH', labelKey: 'agent.creditPurposeCash' },
    { key: 'CONSOLIDATION', labelKey: 'agent.creditPurposeConsolidation' },
    { key: 'OTHER', labelKey: 'agent.creditPurposeOther' },
  ],
};

/** One contact action tile (Zadzwoń / Napisz) under the agent card. */
function ContactAction({
  icon,
  label,
  onPress,
  primary,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  onPress: () => void;
  primary?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => ({
        flex: 1,
        alignItems: 'center',
        gap: spacing.base,
        paddingVertical: spacing.md,
        borderRadius: radius.button,
        backgroundColor: primary ? (pressed ? colors.primaryPressed : colors.primary) : colors.primaryTile,
        transform: [{ scale: pressed ? 0.98 : 1 }],
      })}
    >
      <Ionicons name={icon} size={22} color={primary ? '#FFFFFF' : colors.navy} />
      <AppText variant="labelLg" color={primary ? '#FFFFFF' : colors.navy}>
        {label}
      </AppText>
    </Pressable>
  );
}

/**
 * "Twój agent" — mock: tw_j_agent_warianty. Explains the two specialists a
 * client can reach through Polfinance and, once the account is linked to a
 * CRM customer, shows the assigned caretaker with real contact actions.
 * Variants: guest / logged-in-not-linked -> CTA to connect; linked -> card.
 */
export default function AgentScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const linked = account?.linkStatus === 'LINKED';
  // Credit-specialist ask: a small form -> lead to the office (same endpoint as
  // the protection ask; a rich summary text routes it and gives the office a
  // starting point). Web/native alike.
  const [creditState, setCreditState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [creditKind, setCreditKind] = useState<CreditKind | null>(null);
  const [creditPurpose, setCreditPurpose] = useState<string | null>(null);
  const [creditAmountRaw, setCreditAmountRaw] = useState('');
  const [creditNote, setCreditNote] = useState('');

  const submitCredit = async () => {
    if (!linked) {
      router.push((account ? '/auth/connect' : '/auth/login') as never);
      return;
    }
    if (!creditKind) return;
    const kindLabel = creditKind === 'MORTGAGE' ? t('agent.creditKindMortgage') : t('agent.creditKindCash');
    const purposeLabel = creditPurpose
      ? t(CREDIT_PURPOSES[creditKind].find((p) => p.key === creditPurpose)?.labelKey ?? '')
      : '—';
    const amount = creditAmountRaw.replace(/[^\d]/g, '');
    const summary =
      `Zapytanie kredytowe z aplikacji.\n` +
      `Rodzaj: ${kindLabel}.\n` +
      `Cel: ${purposeLabel}.\n` +
      `Kwota: ${amount ? `${amount} zł` : 'nie podano'}.\n` +
      `Notatka klienta: ${creditNote.trim() || 'brak'}.`;
    setCreditState('sending');
    const res = await requestProtectionQuoteApi(summary);
    setCreditState(res.ok ? 'sent' : 'error');
  };

  const [agent, setAgent] = useState<AgentCardData | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!linked) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await fetchAgentApi();
      if (!cancelled) {
        setAgent(res.ok && res.data?.name ? res.data : null);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linked]);

  // Where "podłącz konto" should lead: guests must log in first, a logged-in
  // account goes straight to the linking flow.
  const connectTarget = account ? '/auth/connect' : '/auth/login';

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      {/* The credit form sits low on the page — without this the keyboard froze
          scrolling while typing the amount (same pattern as policy/new.tsx). */}
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}
      >
        <ScreenHeader title={t('agent.title')} />

        <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
          {t('agent.specialistsHeader')}
        </AppText>

        {/* Insurance agent: explains WHY linking pays off (personal caretaker) */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <IconTile icon="shield-outline" bg={colors.primaryTile} fg={colors.primary} size={48} />
            <View style={{ flex: 1 }}>
              <AppText variant="headlineSm">{t('agent.insuranceTitle')}</AppText>
              <AppText variant="bodyMd">{t('agent.insuranceSubtitle')}</AppText>
            </View>
          </View>
          <AppText variant="bodyMd">{t('agent.insuranceBody')}</AppText>

          {linked && loaded && agent ? (
            /* Linked + assigned: the caretaker business card */
            <View style={{ gap: spacing.sm, borderTopWidth: 1, borderTopColor: colors.divider, paddingTop: spacing.sm }}>
              <AppText variant="labelMd" style={{ letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {t('agent.caretakerHeader')}
              </AppText>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                <View
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: radius.full,
                    backgroundColor: colors.navy,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AppText variant="headlineSm" color="#FFFFFF">
                    {agent.name?.charAt(0).toUpperCase()}
                  </AppText>
                </View>
                <View style={{ flex: 1 }}>
                  <AppText variant="headlineSm">{agent.name}</AppText>
                  <AppText variant="bodyMd" color={colors.primary}>
                    {t('agent.caretakerRole')}
                  </AppText>
                </View>
              </View>
              {/* Chat first — the fastest way to reach the agent (owner). Always
                  available for a linked account; call/SMS need a phone. */}
              <PrimaryButton
                label={t('agent.startChat')}
                onPress={() => router.push('/chat')}
              />
              {agent.phone ? (
                <View style={{ flexDirection: 'row', gap: spacing.sm }}>
                  <ContactAction
                    icon="call-outline"
                    label={t('agent.call')}
                    onPress={() => void Linking.openURL(`tel:${agent.phone}`)}
                  />
                  <ContactAction
                    icon="chatbox-outline"
                    label={t('agent.text')}
                    onPress={() => void Linking.openURL(`sms:${agent.phone}`)}
                  />
                </View>
              ) : (
                <AppText variant="bodyMd">{t('agent.noPhone')}</AppText>
              )}
            </View>
          ) : linked && loaded && !agent ? (
            /* Linked but no agent assigned in the CRM yet */
            <View
              style={{
                flexDirection: 'row',
                gap: spacing.sm,
                backgroundColor: colors.disabledBg,
                borderRadius: radius.chip,
                padding: spacing.md,
              }}
            >
              <Ionicons name="information-circle-outline" size={20} color={colors.navy} />
              <AppText variant="bodyMd" style={{ flex: 1 }}>
                {t('agent.noAgentYet')}
              </AppText>
            </View>
          ) : !linked ? (
            /* Guest / not linked: infobox + connect CTA (mock variant) */
            <View style={{ gap: spacing.sm }}>
              <View
                style={{
                  flexDirection: 'row',
                  gap: spacing.sm,
                  backgroundColor: colors.disabledBg,
                  borderRadius: radius.chip,
                  padding: spacing.md,
                }}
              >
                <Ionicons name="information-circle-outline" size={20} color={colors.navy} />
                <AppText variant="bodyMd" style={{ flex: 1 }}>
                  {t('agent.notLinkedInfo')}
                </AppText>
              </View>
              <PrimaryButton
                label={t('agent.connectCta')}
                onPress={() => router.push(connectTarget as never)}
              />
            </View>
          ) : null}
        </Card>

        {/* Credit specialist: second relationship a client can ask for */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <IconTile icon="cash-outline" bg={colors.warningBg} fg={colors.warning} size={48} />
            <View style={{ flex: 1 }}>
              <AppText variant="headlineSm">{t('agent.creditTitle')}</AppText>
              <AppText variant="bodyMd">{t('agent.creditSubtitle')}</AppText>
            </View>
          </View>
          <AppText variant="bodyMd">{t('agent.creditBody')}</AppText>
          {creditState === 'sent' ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <Ionicons name="checkmark-circle-outline" size={22} color={colors.primary} />
              <AppText variant="bodyLg" color={colors.navy} style={{ flex: 1 }}>
                {t('agent.creditSent')}
              </AppText>
            </View>
          ) : !linked ? (
            <PrimaryButton label={t('agent.creditCtaConnect')} onPress={submitCredit} />
          ) : (
            <View style={{ gap: spacing.sm }}>
              {/* Kind */}
              <AppText variant="labelLg">{t('agent.creditKindLabel')}</AppText>
              <View style={{ flexDirection: 'row', gap: spacing.xs }}>
                {(['MORTGAGE', 'CASH'] as CreditKind[]).map((k) => {
                  const active = creditKind === k;
                  return (
                    <Pressable
                      key={k}
                      accessibilityRole="button"
                      onPress={() => {
                        setCreditKind(k);
                        setCreditPurpose(null);
                      }}
                      style={{
                        flex: 1,
                        alignItems: 'center',
                        paddingVertical: spacing.sm,
                        borderRadius: radius.button,
                        backgroundColor: active ? colors.navy : colors.background,
                      }}
                    >
                      <AppText variant="labelLg" color={active ? '#FFFFFF' : colors.navy}>
                        {k === 'MORTGAGE' ? t('agent.creditKindMortgage') : t('agent.creditKindCash')}
                      </AppText>
                    </Pressable>
                  );
                })}
              </View>

              {/* Purpose (depends on kind) */}
              {creditKind && (
                <>
                  <AppText variant="labelLg">{t('agent.creditPurposeLabel')}</AppText>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs }}>
                    {CREDIT_PURPOSES[creditKind].map((p) => {
                      const active = creditPurpose === p.key;
                      return (
                        <Pressable
                          key={p.key}
                          accessibilityRole="button"
                          onPress={() => setCreditPurpose(p.key)}
                          style={{
                            backgroundColor: active ? colors.primary : colors.background,
                            borderRadius: radius.full,
                            paddingHorizontal: spacing.md,
                            paddingVertical: spacing.xs,
                          }}
                        >
                          <AppText variant="labelMd" color={active ? '#FFFFFF' : colors.navy}>
                            {t(p.labelKey)}
                          </AppText>
                        </Pressable>
                      );
                    })}
                  </View>

                  <TextField
                    label={t('agent.creditAmountLabel')}
                    placeholder="np. 350 000"
                    keyboardType="numeric"
                    value={creditAmountRaw}
                    onChangeText={setCreditAmountRaw}
                  />
                  <TextField
                    label={t('agent.creditNoteLabel')}
                    placeholder={t('agent.creditNotePlaceholder')}
                    value={creditNote}
                    onChangeText={setCreditNote}
                  />
                  <PrimaryButton
                    label={creditState === 'sending' ? t('agent.creditSending') : t('agent.creditSubmit')}
                    disabled={!creditKind || creditState === 'sending'}
                    onPress={submitCredit}
                  />
                </>
              )}
            </View>
          )}
          {creditState === 'error' && (
            <AppText variant="labelMd" color={colors.error}>
              {t('agent.creditError')}
            </AppText>
          )}
        </Card>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
